/**
 * Re-home: long standalone NextGen MCQs -> JDS flashcards `multiple_choice`.
 *
 * WHY THIS MODULE EXISTS, AND WHY IT LIVES IN `src/lib/` RATHER THAN `scripts/`
 * ---------------------------------------------------------------------------
 * Two consumers need the SAME two facts and must not drift apart:
 *
 *   1. `scripts/rehome-nextgen-mcqs.ts` (the ingest) needs the field map.
 *   2. `src/components/flashcards/card-types/MultipleChoiceCard.tsx` (the render)
 *      needs to know whether a card came from here, so it can pick the long-stem
 *      layout.
 *
 * `vitest.config.ts` only includes `src/**` and `services/payment/tests/**`, so a
 * mapping module under `scripts/` would be structurally untestable and outside
 * `tsc -b`. Putting the pure logic here makes both consumers share one definition
 * of the marker and gives the field map real test coverage.
 * See `.claude/rules/propagate-the-fix.md` — one concept, one location.
 *
 * NOTHING IN THIS MODULE MAY PRODUCE USER-VISIBLE NCBE VOCABULARY.
 * `ncbe_tasks` and `skill_domain` are carried in card metadata for the learning
 * signal payload (intelligence-specialist owns that design; we carry the fields,
 * we do not emit anything new). They are never composed into `question`,
 * `answer`, or any option text. Nor is the word "NextGen". Marketing's hard ban
 * list is on board task ZFugLgljMoxq3W5sjaW2.
 */

// ---------------------------------------------------------------------------
// Markers — the single source of truth for "this card came from the re-home"
// ---------------------------------------------------------------------------

/**
 * `flashcards.source_type` for re-homed cards. CANONICAL: the partial unique
 * index that makes the ingest idempotent is defined on
 * `(source_id) WHERE source_type = 'nextgen_length_legacy'`.
 */
export const LENGTH_LEGACY_SOURCE_TYPE = 'nextgen_length_legacy';

/**
 * `nextgen_questions.retirement_reason` value the ingest selects on. The column
 * is added by board task K3rT50XXnNlNXzQI698A (database-specialist); it does NOT
 * exist yet. Verified absent in prod 2026-09-03.
 */
export const LENGTH_LEGACY_RETIREMENT_REASON = 'length_legacy_2026';

/**
 * The one official collection these cards land in. Mirrors the UUID in
 * `sql/migrations/20260903_applied_practice_collection.proposed.sql`.
 */
export const APPLIED_PRACTICE_COLLECTION_ID = 'a9910000-a991-4000-9000-000000000001';

/**
 * Below this many characters in `flashcards.question`, the default (short-card)
 * layout is used and nothing changes. ~600 chars is roughly 100 words — far
 * above any recall/definition card and far below the shortest re-homed item
 * (201 words, ~1,300 chars). The measured re-homed mean is 267 words (~1,750
 * chars) and the max is 481 words.
 *
 * The length fallback exists so the layout is correct for ANY long stem, not
 * only re-homed ones — a card is unreadable because it is long, not because of
 * where it came from.
 */
export const LONG_STEM_CHAR_THRESHOLD = 600;

// ---------------------------------------------------------------------------
// Source shape — the narrow projection the ingest SELECTs from nextgen_questions
// ---------------------------------------------------------------------------

export interface NextGenSourceOption {
  id: string;
  text: string;
  is_correct: boolean;
}

export interface NextGenSourceRow {
  id: string;
  exam_format: string;
  question_type: string | null;
  mcq_subtype: string | null;
  select_count: number | null;
  role_assignment: string | null;
  fact_pattern: string | null;
  provided_resource: string | null;
  stem: string;
  options: NextGenSourceOption[] | null;
  explanations: {
    correct_answer?: { option_id: string; summary?: string; detailed?: string } | null;
    distractors?: Array<{
      option_id: string;
      summary?: string;
      detailed?: string;
      why_tempting?: string;
      cognitive_trap?: string;
    }> | null;
  } | null;
  subjects: string[] | null;
  subtopics: string[] | null;
  skill_domains: string[] | null;
  ncbe_task_mappings: Array<{ task_number: number; relevance?: string }> | null;
  difficulty_tier: string | null;
  estimated_time_seconds: number | null;
  retirement_reason: string | null;
  lineage_id: string | null;
}

// ---------------------------------------------------------------------------
// Target shape — one planned flashcards row plus its junction rows
// ---------------------------------------------------------------------------

export interface PlannedAnswerOption {
  id: string;
  text: string;
  explanation?: string;
}

export interface PlannedCard {
  /** Deterministic: equals the source question id, so re-runs are no-ops. */
  source_id: string;
  source_type: string;
  card_type: 'multiple_choice';
  question: string;
  answer: string;
  answer_options: PlannedAnswerOption[];
  correct_answer_id: string;
  difficulty_level: 'EASY' | 'MEDIUM' | 'HARD' | null;
  is_official: true;
  is_public_sample: false;
  created_by: null;
  metadata: Record<string, unknown>;
  source_metadata: Record<string, unknown>;
  /** Canonical subject slugs, for `flashcard_subjects`. */
  subjectSlugs: string[];
  collectionId: string;
}

export class RehomeMappingError extends Error {
  constructor(public readonly sourceId: string, message: string) {
    super(`[${sourceId}] ${message}`);
    this.name = 'RehomeMappingError';
  }
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * LLM-written text fields arrive with literal "None"/"null"/"N/A" strings often
 * enough that a plain truthiness check is wrong — a 4-character "None" survives
 * `.trim()` and renders as a resource block containing the word None.
 * `.claude/rules/silent-degradation.md` §6, "an instrument can manufacture the
 * defect it checks for".
 */
const NULLISH_TEXT = new Set(['', 'none', 'null', 'n/a', 'na', '-', 'nil', 'undefined']);

export function cleanText(value: string | null | undefined): string | null {
  if (value === null || value === undefined) return null;
  const trimmed = value.trim();
  if (NULLISH_TEXT.has(trimmed.toLowerCase())) return null;
  return trimmed;
}

const DIFFICULTY_TIER_TO_LEVEL: Record<string, 'EASY' | 'MEDIUM' | 'HARD'> = {
  foundation: 'EASY',
  proficient: 'MEDIUM',
  exam: 'HARD',
};

/** Render text as a markdown blockquote, prefixing EVERY line (blank ones too). */
function asBlockquote(text: string): string {
  return text
    .split('\n')
    .map((line) => (line.trim() === '' ? '>' : `> ${line}`))
    .join('\n');
}

/**
 * Compose the card body.
 *
 * Structure, and why each piece is shaped this way:
 *
 *   {role_assignment}          plain paragraph — dropping it leaves "your client"
 *                              with no antecedent; every source row has one
 *   {fact_pattern}             plain paragraphs — the bulk of the 267-word mean
 *   > **From the materials**   blockquote — the ~59-word statute/case excerpt,
 *   > {provided_resource}      present on only 37% of rows, so it is conditional
 *   **{stem}**                 the call of the question, bolded
 *
 * The call is BOLD rather than positionally last on purpose. The long-stem
 * renderer styles `strong`, so it never has to know which paragraph is the call
 * — no positional parsing to break when a fact pattern happens to end in a
 * question mark. And because `question` holds the whole item, any other renderer
 * (or a plain-text export) still shows the student what is being asked.
 */
export function composeQuestionMarkdown(row: NextGenSourceRow): string {
  const parts: string[] = [];

  const role = cleanText(row.role_assignment);
  if (role) parts.push(role);

  const facts = cleanText(row.fact_pattern);
  if (facts) parts.push(facts);

  const resource = cleanText(row.provided_resource);
  if (resource) {
    parts.push(asBlockquote(`**From the provided materials**\n\n${resource}`));
  }

  const stem = cleanText(row.stem);
  if (!stem) throw new RehomeMappingError(row.id, 'stem is empty after normalization');
  parts.push(`**${stem}**`);

  return parts.join('\n\n');
}

/**
 * Distractor explanations: RENDERED, not dropped. See the module note on the
 * board task for the reasoning. The per-option string is composed here so the
 * component never has to know NextGen's explanation shape.
 *
 * `cognitive_trap` and `distractor_formula` are deliberately EXCLUDED — they are
 * authoring-pipeline vocabulary, not student-facing prose.
 */
function composeDistractorExplanation(d: {
  summary?: string;
  detailed?: string;
  why_tempting?: string;
}): string | undefined {
  const body = cleanText(d.detailed) ?? cleanText(d.summary);
  if (!body) return undefined;
  const tempting = cleanText(d.why_tempting);
  return tempting ? `${body}\n\n_Why it's tempting:_ ${tempting}` : body;
}

// ---------------------------------------------------------------------------
// The field map
// ---------------------------------------------------------------------------

/**
 * Map one source row to one planned flashcards row.
 *
 * THROWS rather than returning a degraded card. A card whose `correct_answer_id`
 * does not match any option renders as permanently unanswerable with no error
 * (`getCorrectAnswerId` returns null and every selection compares false), which
 * is exactly the plausible-looking-but-broken output
 * `.claude/rules/silent-degradation.md` exists to prevent. The caller counts and
 * reports rejects; it never inserts them.
 *
 * @param normalizeSubjectFn injected (rather than imported) so the test can
 *        assert the mapping calls it, and so this module stays free of the
 *        subject table's UUID list.
 */
export function mapSourceRowToCard(
  row: NextGenSourceRow,
  normalizeSubjectFn: (input: string) => string | undefined,
  collectionId: string = APPLIED_PRACTICE_COLLECTION_ID,
): PlannedCard {
  // `source_id` is a uuid column and is interpolated into generated SQL (both as
  // a literal and as a `--` comment label). Validate the shape here rather than
  // trusting it, because the ingest can also be fed a hand-edited JSON file.
  if (!/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(row.id ?? '')) {
    throw new RehomeMappingError(String(row.id), 'id is not a well-formed uuid');
  }

  // --- Gate 3 of 3. Gates 1 and 2 (`exam_format='mcq'`, `select_count=1`) are
  // in the SELECT. This one is here because it is the only gate that reads the
  // options themselves, and it is the one that actually decides gradability.
  // Do NOT relax it to "at least one": MultipleChoiceCard holds a single
  // `selectedAnswerId`, so a two-key item marks every attempt wrong.
  const options = row.options ?? [];
  if (options.length < 2) {
    throw new RehomeMappingError(row.id, `expected >=2 options, got ${options.length}`);
  }
  const correct = options.filter((o) => o.is_correct);
  if (correct.length !== 1) {
    throw new RehomeMappingError(
      row.id,
      `expected exactly 1 correct option (single-select card), got ${correct.length}`,
    );
  }
  if (row.select_count !== null && row.select_count !== 1) {
    throw new RehomeMappingError(row.id, `select_count=${row.select_count}, expected 1`);
  }

  const correctOption = correct[0];
  const correctText = cleanText(correctOption.text);
  if (!correctText) {
    throw new RehomeMappingError(row.id, 'correct option has empty text');
  }

  // A distractor whose text is empty (or the literal "None") would render as a
  // blank selectable option — gradable, plausible, and wrong.
  for (const o of options) {
    if (!cleanText(o.text)) {
      throw new RehomeMappingError(row.id, `option ${o.id} has empty text`);
    }
  }

  // --- Explanations, keyed by option id.
  const correctExplanationSrc = row.explanations?.correct_answer ?? null;

  // TWO SOURCES OF TRUTH for "which option is correct": `options[].is_correct`
  // and `explanations.correct_answer.option_id`. Reading only the first means a
  // disagreement produces a card keyed to one letter whose explanation argues
  // for another — gradable, plausible, and wrong, which is the exact shape
  // `.claude/rules/silent-degradation.md` exists to catch. Reconcile, don't pick.
  if (correctExplanationSrc?.option_id && correctExplanationSrc.option_id !== correctOption.id) {
    throw new RehomeMappingError(
      row.id,
      `correct-answer disagreement: options[].is_correct says ${correctOption.id}, ` +
        `explanations.correct_answer.option_id says ${correctExplanationSrc.option_id}`,
    );
  }

  const correctExplanation =
    cleanText(correctExplanationSrc?.detailed) ?? cleanText(correctExplanationSrc?.summary);

  const optionIds = new Set(options.map((o) => o.id));
  const distractorById = new Map<string, string>();
  const orphanedExplanationIds: string[] = [];
  for (const d of row.explanations?.distractors ?? []) {
    if (!optionIds.has(d.option_id)) {
      // An explanation keyed to an option that does not exist is a source-data
      // defect. Dropping it silently is how you lose the fact that it happened.
      orphanedExplanationIds.push(d.option_id);
      continue;
    }
    const composed = composeDistractorExplanation(d);
    if (composed) distractorById.set(d.option_id, composed);
  }
  if (orphanedExplanationIds.length) {
    throw new RehomeMappingError(
      row.id,
      `distractor explanation(s) keyed to non-existent option(s): ${orphanedExplanationIds.join(', ')}`,
    );
  }

  // --- Options. Stored order is preserved. It does NOT reach the student: every
  // flashcard surface shuffles at display time, seeded by card id, and assigns
  // A/B/C/D from the post-shuffle position (src/lib/flashcard-option-order.ts).
  // So correct-first storage is harmless, and grading keys on `option.id` only.
  const answer_options: PlannedAnswerOption[] = options.map((o) => {
    const explanation =
      o.id === correctOption.id ? correctExplanation ?? undefined : distractorById.get(o.id);
    return {
      id: o.id, // uppercase A-D verbatim; correct_answer_id below must match exactly
      text: cleanText(o.text) ?? o.text,
      ...(explanation ? { explanation } : {}),
    };
  });

  // --- Subjects. `normalizeSubject` returns its input unchanged for unknown
  // values (documented behaviour), so an unmapped subject would silently become
  // a slug nothing resolves. Dedupe and let the caller report the distinct set.
  const subjectSlugs = Array.from(
    new Set(
      (row.subjects ?? [])
        .map((s) => normalizeSubjectFn(s))
        .filter((s): s is string => Boolean(s)),
    ),
  );

  const ncbeTasks = (row.ncbe_task_mappings ?? [])
    .map((m) => m.task_number)
    .filter((n) => Number.isFinite(n));

  const primarySubject = subjectSlugs[0] ?? null;
  const topic = cleanText(row.subtopics?.[0]) ?? null;
  const skillDomain = cleanText(row.skill_domains?.[0]) ?? null;

  return {
    source_id: row.id,
    source_type: LENGTH_LEGACY_SOURCE_TYPE,
    card_type: 'multiple_choice',
    question: composeQuestionMarkdown(row),

    // `answer` is NOT NULL. It is EXACTLY the correct option's text and nothing
    // more, which suppresses the component's "Answer:" block
    // (MultipleChoiceCard.tsx renders it only when `card.answer !== correctOption.text`).
    //
    // An earlier version appended the explanation here. That looked harmless and
    // was not: `explanation` in the component already resolves to the correct
    // option's explanation, so the student read the identical paragraph twice
    // under two headings — "Explanation:" and "Answer:" — with the correct
    // option's full text, already on screen in green, sandwiched between them.
    // Caught in ui review; the fix belongs at ingest, not in the component.
    answer: correctText,

    answer_options,
    correct_answer_id: correctOption.id,
    difficulty_level: row.difficulty_tier
      ? DIFFICULTY_TIER_TO_LEVEL[row.difficulty_tier] ?? null
      : null,
    is_official: true,
    is_public_sample: false,
    created_by: null,

    // Carried for the learning-signal payload (intelligence-specialist owns the
    // emit design; nothing here emits). NEVER displayed — see module header.
    // `origin` mirrors `source_type` and `source_question_id` mirrors `source_id`
    // by explicit request, so the signal consumer reads one object; the COLUMNS
    // are canonical (the unique index lives on them).
    metadata: {
      origin: LENGTH_LEGACY_SOURCE_TYPE,
      source_question_id: row.id,
      subject: primarySubject,
      subjects: subjectSlugs,
      topic,
      skill_domain: skillDomain,
      skill_domains: row.skill_domains ?? [],
      ncbe_tasks: ncbeTasks,
      provided_resource: cleanText(row.provided_resource),
      recommended_time: row.estimated_time_seconds,
    },

    // Audit trail — how to get back to the source row and prove no leak-back.
    source_metadata: {
      retirement_reason: row.retirement_reason,
      lineage_id: row.lineage_id,
      mcq_subtype: row.mcq_subtype,
      question_type: row.question_type,
      exam_format: row.exam_format,
      difficulty_tier: row.difficulty_tier,
    },

    subjectSlugs,
    collectionId,
  };
}

// ---------------------------------------------------------------------------
// Render-side selector — consumed by MultipleChoiceCard
// ---------------------------------------------------------------------------

/** The subset of a Flashcard this module needs. Keeps the card component free
 *  to pass its full `Flashcard` without this module importing the UI types. */
export interface LongStemCandidate {
  question?: string | null;
  source_type?: string | null;
  metadata?: unknown;
}

export function isLengthLegacyCard(card: LongStemCandidate): boolean {
  if (card.source_type === LENGTH_LEGACY_SOURCE_TYPE) return true;
  const meta = card.metadata as Record<string, unknown> | null | undefined;
  return meta?.origin === LENGTH_LEGACY_SOURCE_TYPE;
}

/**
 * True when the card should render with the long-stem layout: body-size prose,
 * a quoted excerpt block, and per-option explanations after reveal.
 *
 * Deliberately NOT a pure length check. Origin wins so a re-homed item on the
 * short end still gets the layout its structure needs (blockquote excerpt,
 * distractor explanations); length is the fallback so any long card benefits.
 *
 * Short cards are untouched by construction, and this was VERIFIED against prod
 * rather than argued: of the 970 cards that reach MultipleChoiceCard (589
 * `multiple_choice` + 381 `true_false` — the component serves both), ZERO are at
 * or above 600 chars (longest is 354, 1.7x headroom), none carry a `source_type`
 * marker, and `metadata->>'origin'` is null on every row in the table. All three
 * entry conditions are dead on existing data, so the default branch is reached
 * unchanged. Re-measure before lowering the threshold.
 *
 * Note the threshold measures the MARKDOWN SOURCE, not rendered text — `> `
 * prefixes and `**` inflate it slightly. That errs toward the long layout, which
 * is the safe direction.
 */
export function shouldUseLongStemLayout(card: LongStemCandidate): boolean {
  if (isLengthLegacyCard(card)) return true;
  return (card.question?.length ?? 0) >= LONG_STEM_CHAR_THRESHOLD;
}
